import { Skeleton } from "@/components/ui/skeleton";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import type { AgentPulseLeaderboardEntry } from "@forgepoint/shared";
import { LeaderboardList } from "./LeaderboardList";
import { LastWeekLeaderFooter } from "./LastWeekLeaderFooter";

interface LastWeekLeader {
  userName: string;
  messageCount: number;
}

interface UserLeaderboardProps {
  entries: AgentPulseLeaderboardEntry[];
  lastWeekLeader: LastWeekLeader | null;
  isLoading: boolean;
}

/**
 * Displays a ranked list of users by message count with trend indicators
 * and anomaly badges. Designed as a compact sidebar widget.
 * @param props - Leaderboard entries and loading state
 * @returns A ranked user list or appropriate empty/loading state
 */
export function UserLeaderboard({
  entries,
  lastWeekLeader,
  isLoading,
}: UserLeaderboardProps) {
  const agentName = useStandardAgentDisplayName();

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Team Engagement
        </h3>
        <span className="ml-auto font-mono text-[11px] text-muted-foreground/70">
          since last pulse
        </span>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : entries.length === 0 ? (
        <EmptyState agentName={agentName} />
      ) : (
        <LeaderboardList entries={entries} />
      )}

      {lastWeekLeader && <LastWeekLeaderFooter leader={lastWeekLeader} />}
    </div>
  );
}

function EmptyState({ agentName }: { agentName: string }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-10 text-center">
      <p className="text-sm text-muted-foreground">
        No messages since last pulse
      </p>
      <p className="mt-1 text-xs text-muted-foreground/70">
        Activity will appear here once team members start messaging {agentName}.
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-2.5">
          <Skeleton className="h-[22px] w-[22px] rounded-md" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-3 w-8" />
        </div>
      ))}
    </div>
  );
}
